import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFilters, setLevel, setSubject } from "../../../../store/search";
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import { FilterContext } from "../FilterContext/FilterContext";
import axios from "axios";

export const SearchBars = () => {
  const {
    subject,
    setSubject,
    level,
    setLevel,
    paymentMethod,
    setPaymentMethod,
  } = useContext(FilterContext);

  const [subjects, setSubjects] = useState([]);

  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [availableLevels, setAvailableLevels] = useState([]);
  const [isFetchingSubjects, setIsFetchingSubjects] = useState(false);

  const dispatch = useDispatch(); // Use the dispatch hook
  const setSelectedLevel = (value) => dispatch(setFilters({ level: value }));

  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER}/api/subjects`
        );
        setSubjects(response.data.subjects);
        setFilteredSubjects(response.data.subjects);
      } catch (error) {
        console.error("Error fetching subjects:", error);
      }
    };

    fetchSubjects();
  }, []);

  const handleSubjectChange = (_, value) => {
    setSelectedSubject(value);
    setSubject(value);
    setSelectedLevel(""); // Reset the level when a new subject is selected
    const subjectData = subjects.find((sub) => sub.name === value);
    setAvailableLevels(subjectData ? subjectData.levels : []);
    setLevel(""); // Update the state with the selected value

    // Update the Redux filters with the selected subject
    dispatch(setFilters({ subject: value, level: "" })); // Clear level as subject changes
  };

  const handleSubjectInputChange = async (_, value) => {
    if (value.length > 2) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER}/api/subjects?search=${value}`
        );
        setFilteredSubjects(response.data.subjects);
      } catch (error) {
        console.error("Error filtering subjects:", error);
      }
    } else {
      setFilteredSubjects(subjects);
    }
  };

  // Handle level selection and update filters
  const handleLevelChange = (e) => {
    const selectedLevel = e.target.value;
    setSelectedLevel(selectedLevel);
    setLevel(e.target.value); // Update the state with the selected value

    // Update Redux store with selected level
    dispatch(setFilters({ level: selectedLevel }));
  };

  const handlePaymentChange = (e) => {
    const value = e.target.value;
    const isChecked = e.target.checked;
    const updatedPaymentMethod = isChecked
      ? [...paymentMethod, value]
      : paymentMethod.filter((method) => method !== value);

    setPaymentMethod(updatedPaymentMethod);
  };

  const { subject: reduxSubject, level: reduxLevel } = useSelector(
    (state) => state.search.filters
  );

  useEffect(() => {
    const runOnReduxSubject = async () => {
      if (reduxSubject && reduxSubject.length > 2) {
        setSelectedSubject(reduxSubject);
        setSubject(reduxSubject); // ✅ sync Redux → FilterContext
        setLevel(reduxLevel || "");

        const subjectData = subjects.find((sub) => sub.name === reduxSubject);
        setAvailableLevels(subjectData ? subjectData.levels : []);

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_SERVER}/api/subjects?search=${reduxSubject}`
          );
          setFilteredSubjects(response.data.subjects);
        } catch (error) {
          console.error("Error fetching filtered subjects from redux:", error);
        }
      }
    };

    runOnReduxSubject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxSubject, reduxLevel, subjects]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        gap: 2,
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
          flexDirection: {
            lg: "row",
            md: "column",
            sm: "column",
            xs: "column",
          },
        }}
      >
        {/* Subject Dropdown */}
        <Autocomplete
          options={filteredSubjects.map((subject) => subject.name)}
          value={subject || ""}
          onInputChange={handleSubjectInputChange}
          onChange={handleSubjectChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Enter your subject"
              variant="outlined"
              className="subject-input"
            />
          )}
        />

        {/* Level Dropdown */}
        {subject && (
          <TextField
            select
            label="Level"
            value={level || ""}
            variant="outlined"
            onChange={handleLevelChange}
            className="level-input"
          >
            {availableLevels.map((lvl) => (
              <MenuItem key={lvl} value={lvl}>
                {lvl}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* Free/Paid Filters */}
        <FormControl component="fieldset" className="filter-section-end">
          <FormControlLabel
            control={
              <Checkbox
                checked={paymentMethod.includes("free")}
                onChange={handlePaymentChange}
                value="free"
              />
            }
            label="Free"
            sx={{ "& .MuiFormControlLabel-label": { marginLeft: "10px" } }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={paymentMethod.includes("paid")}
                onChange={handlePaymentChange}
                value="paid"
              />
            }
            label="Paid"
            sx={{ "& .MuiFormControlLabel-label": { marginLeft: "10px" } }}
          />
        </FormControl>
      </Box>
    </Box>
  );
};
